import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Icon, Spinner } from '@penta/penta-shared';
import { usePentaRq } from '@penta/penta-api';
import { formatDate, formatAmount } from '@penta/penta-shared';
import styles from './$paymentId.module.scss';
import { useClient } from '../../../../contexts/ClientContext';

export const Route = createFileRoute(
  '/$locale/smartbanking/payments/$paymentId',
)({
  component: PaymentDetail,
});

export function PaymentDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { paymentId, locale } = Route.useParams();

  // Using usePentaRq hook for API
  const { selectedClientId } = useClient();
  const api = usePentaRq();

  // Convert paymentId string to number for API call
  const idAsNumber = Number(paymentId);
  const isValidId = !Number.isNaN(idAsNumber) && idAsNumber > 0;

  const { data: payment, isLoading } = api.useQuery(
    'get',
    '/payment/{id}',
    {
      params: {
        path: {
          id: idAsNumber,
        },
      },
    },
    {
      enabled: isValidId && selectedClientId !== null,
    },
  );

  const loadingStyle = useMemo(
    () => ({ display: 'flex', justifyContent: 'center', padding: '4rem' }),
    [],
  );

  if (isLoading) {
    return (
      <div style={loadingStyle}>
        <Spinner />
      </div>
    );
  }

  if (!isValidId || !payment) {
    return (
      <div className={styles.paymentDetail}>
        <div className={styles.header}>
          <button
            onClick={() =>
              navigate({
                to: '/$locale/smartbanking/payments',
                params: { locale },
              })
            }
            className={styles.backButton}
            aria-label={t('general.back', 'Zpět')}
          >
            <Icon name="arrow-left" size={24} />
          </button>
          <h1>{t('payments.notFound', 'Platba nenalezena')}</h1>
        </div>
      </div>
    );
  }

  // Formatting strings
  const createdDate = payment.createdDate;
  const formattedAmount = formatAmount(
    Math.abs(payment.paymentOrder?.amount ?? 0),
  );
  const currency = payment.paymentOrder?.currency || 'CZK';

  // Extracting data to display
  const title =
    (payment.paymentOrder?.amount ?? 0) < 0
      ? t('payments.outgoingPayment', 'Odchozí platba')
      : t('payments.incomingPayment', 'Příchozí platba');

  const subtitleDate = createdDate ? formatDate(createdDate) : '';
  const subtitleAccount = payment.paymentOrder?.accountId
    ? `· ${payment.paymentOrder.accountId}`
    : '';

  return (
    <div className={styles.paymentDetail}>
      <header className={styles.header}>
        <button
          onClick={() =>
            navigate({
              to: '/$locale/smartbanking/payments',
              params: { locale },
            })
          }
          className={styles.backButton}
          aria-label={t('general.back', 'Zpět')}
        >
          <Icon name="arrow-left" size={24} />
        </button>
        <div>
          <h1>{title}</h1>
          <div className={styles.subtitle}>
            {subtitleDate} {subtitleAccount}
          </div>
        </div>
      </header>

      <section className={styles.content}>
        <h2 className={styles.contentTitle}>
          {t('payments.paymentDetail', 'Detail platby')}
        </h2>

        <dl className={styles.definitionList}>
          <dt>{t('payments.fields.status', 'Stav')}</dt>
          <dd>
            {payment.status
              ? t(`payments.status.${payment.status}`, payment.status)
              : '-'}
          </dd>

          <dt>{t('payments.fields.transactionId', 'Id transakce')}</dt>
          <dd>{payment.id}</dd>

          {payment.paymentOrder?.accountId && (
            <>
              <dt>{t('payments.fields.myAccount', 'Můj účet')}</dt>
              <dd>{payment.paymentOrder.accountId}</dd>
            </>
          )}

          {payment.paymentOrder?.partner && (
            <>
              <dt>{t('payments.fields.recipientAccount', 'Účet příjemce')}</dt>
              <dd>
                {payment.paymentOrder.partner.iban}
                {payment.paymentOrder.partner.name
                  ? ` (${payment.paymentOrder.partner.name})`
                  : ''}
              </dd>
            </>
          )}

          <dt>{t('payments.fields.amount', 'Částka')}</dt>
          <dd>
            {formattedAmount} {currency}
          </dd>

          <dt>{t('payments.fields.variableSymbol', 'Variabilní symbol')}</dt>
          <dd>{payment.paymentOrder?.variableSymbol || '-'}</dd>

          <dt>{t('payments.fields.specificSymbol', 'Specifický symbol')}</dt>
          <dd>{payment.paymentOrder?.specificSymbol || '-'}</dd>

          <dt>{t('payments.fields.constantSymbol', 'Konstantní symbol')}</dt>
          <dd>{payment.paymentOrder?.constantSymbol || '-'}</dd>

          <dt>
            {t('payments.fields.messageForRecipient', 'Zpráva pro příjemce')}
          </dt>
          <dd>{payment.paymentOrder?.noteForBeneficiary || '-'}</dd>

          {createdDate && (
            <>
              <dt>{t('payments.fields.creationTime', 'Okamžik vytvoření')}</dt>
              <dd>{new Date(createdDate).toLocaleString(locale)}</dd>
            </>
          )}

          <dt>
            {t('payments.fields.communicationChannel', 'Komunikační kanál')}
          </dt>
          <dd>
            {payment.channel
              ? t(`payments.channel.${payment.channel}`, payment.channel)
              : '-'}
          </dd>

          <dt>
            {t('payments.fields.recipientBankName', 'Název účtu příjemce')}
          </dt>
          <dd>{payment.paymentOrder?.partner?.name || '-'}</dd>

          {payment.paymentOrder?.partner?.bic && (
            <>
              <dt>
                {t(
                  'payments.fields.recipientBankBic',
                  'BIC kód banky příjemce',
                )}
              </dt>
              <dd>{payment.paymentOrder.partner.bic}</dd>
            </>
          )}

          {payment.paymentOrder?.dueDate && (
            <>
              <dt>{t('payments.fields.dueDate', 'Datum splatnosti')}</dt>
              <dd>{formatDate(payment.paymentOrder.dueDate)}</dd>
            </>
          )}

          {payment.paymentOrder?.clientId && (
            <>
              <dt>{t('payments.fields.payerReference', 'Reference plátce')}</dt>
              <dd>{payment.paymentOrder.clientId}</dd>
            </>
          )}

          <dt>{t('payments.fields.instantPayment', 'Okamžitá platba')}</dt>
          <dd>
            {payment.paymentOrder?.isInstantPayment
              ? t('general.yes', 'Ano')
              : t('general.no', 'Ne')}
          </dd>
        </dl>
      </section>
    </div>
  );
}
